import {
  baseQuery,
  CREATE_TENANT,
  GET_ALL_TENANTS,
  ENABLE_DISABLE_TENANT,
  DELETE_TENANT,
} from "@/graphql";
import { RootState } from "@/store";
import { createApi } from "@reduxjs/toolkit/query/react";
import { setSelectedTenant, setTenantManagement } from "./tenant-slice";
import { signedUrlApi } from "../signed-url/signed-url-api";
import { GLOBAL_TENANT_FETCH_MIN_PAGE_SIZE } from "./tenant-constants";

interface DeleteTenantResponse {
  deleteTenant: {
    data: string;
  };
}

interface TenantManagementResponse {
  data: ITenant[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}
interface AddTenantResponse {
  data: boolean;
}
interface EnableDisableTenantResponse {
  data: boolean;
}


type ICreateTenantInput = Pick<
  ITenant,
  "companyName" | "companyEmail" | "businessAddress" | "companyLogo" | "tenantType"
>;
type ICreateTenantWithPages = ICreateTenantInput & { allowedPages?: string[] };

// Define the API
export const tenantManagementApi = createApi({
  reducerPath: "api/tenant",
  tagTypes: ["tenantManagement"],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getAllTenants: builder.query<
    TenantManagementResponse,
      {
        pageNo: number;
        pageSize: number;
        searchKeyword?: string;
      }
    >({
      query: (variables) => ({
          document: GET_ALL_TENANTS,
          variables: { ...variables },
      }),
      providesTags: ["tenantManagement"],
      transformResponse: (response: {
        getAllTenants: TenantManagementResponse;
      }) => response?.getAllTenants,
      onQueryStarted: async (arg, { getState, dispatch, queryFulfilled }) => {
        const state = getState() as RootState;

        const { selectedTenant } = state.tenantManagement;
        const { data, meta } = (await queryFulfilled).data;

        const TenantsWithProfileImage = await Promise.all(
          data.map(async (tenant) => {
            if (tenant.companyLogo) {
              const { signedUrl } = await dispatch(
                signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                  fileName: tenant.companyLogo,
                })
              ).unwrap();

              return { ...tenant, companyLogo: signedUrl };
            }
            return tenant;
          })
        );


        // Only full first-page tenant fetches hydrate the global tenant list.
        // Search and paginated table requests must not reset admin tenant selection.
        const searchKw = (arg.searchKeyword ?? "").trim();
        const shouldSyncGlobal =
          arg.pageNo === 1 &&
          searchKw === "" &&
          arg.pageSize >= GLOBAL_TENANT_FETCH_MIN_PAGE_SIZE;

        if (!shouldSyncGlobal) {
          dispatch(
            tenantManagementApi.util.updateQueryData(
              "getAllTenants",
              arg,
              (draft) => {
                draft.data = TenantsWithProfileImage;
              }
            )
          );
          return;
        }

        dispatch(setTenantManagement(TenantsWithProfileImage));

        // Prefer persisted selection (by ID) if available
        let hydrated = false;
        let storedTenantId: string | null = null;
        try {
          storedTenantId = localStorage.getItem("selectedTenantId");
          if (storedTenantId) {
            const match = TenantsWithProfileImage.find((t) => t._id === storedTenantId);
            if (match && (!selectedTenant || selectedTenant._id !== storedTenantId)) {
              dispatch(setSelectedTenant(match));
              hydrated = true;
            } else if (match && selectedTenant?._id === storedTenantId) {
              hydrated = true;
            } else if (!match && meta.countDocs <= TenantsWithProfileImage.length) {
              localStorage.removeItem("selectedTenantId");
              storedTenantId = null;
            }
          }
        } catch {
          storedTenantId = null;
        }

        // Fallback only when there is no persisted tenant to preserve.
        if (!hydrated && !selectedTenant && TenantsWithProfileImage.length > 0) {
          if (!storedTenantId) {
            dispatch(setSelectedTenant(TenantsWithProfileImage[0]));
          }
        }

        const updatedSelectedTenant = TenantsWithProfileImage.find(
          (tenant) => tenant._id === selectedTenant?._id
        );

        if (
          TenantsWithProfileImage.length > 0 &&
          updatedSelectedTenant &&
          selectedTenant?.companyLogo !== updatedSelectedTenant?.companyLogo
        ) {
          dispatch(setSelectedTenant(updatedSelectedTenant));
        }
      },
    }),
    // Define the createUser mutation
    createTenant: builder.mutation<
      AddTenantResponse,
      { tenantData: ICreateTenantWithPages }
    >({
      query: ({ tenantData }) => {
        return {
          document: CREATE_TENANT,
          variables: { tenantData },
        };
      },
      invalidatesTags: ["tenantManagement"],
    }),
    enableDisableTenant: builder.mutation<
      EnableDisableTenantResponse,
      { tenantId: string; isRestricted: boolean }
    >({
      query: ({ tenantId, isRestricted }) => ({
        document: ENABLE_DISABLE_TENANT,
        variables: { tenantId, isRestricted },
      }),
      invalidatesTags: ["tenantManagement"],
    }),
    deleteTenant: builder.mutation<
      DeleteTenantResponse,
      { tenantId: string }
    >({
      query: ({ tenantId }) => ({
        document: DELETE_TENANT,
        variables: { tenantId },
      }),
      invalidatesTags: ["tenantManagement"],
    }),

  }),
});

// Extract hook
export const {
  useGetAllTenantsQuery,
  useCreateTenantMutation,
  useEnableDisableTenantMutation,
  useDeleteTenantMutation,
} = tenantManagementApi;
